import { LinearGradient } from 'expo-linear-gradient';
import { ArrowRight, BarChart3, Bot, Sparkles, Zap } from 'lucide-react-native';
import type { ComponentType, ReactNode } from 'react';
import { Platform, Pressable, StyleSheet, useWindowDimensions, View } from 'react-native';
import { useTranslation } from '../../contexts/TranslationContext';
import { colors } from '../../theme/colors';
import { spacing } from '../../theme/spacing';
import { tokens } from '../../theme/tokens';
import { ScrollReveal } from '../animations';
import { Container } from '../primitives/Container';
import { Text } from '../primitives/Text';
import { BrandIcon } from './BrandIcon';
import { LandingSectionHeading } from './LandingSectionHeading';
import {
  GLASS_GRADIENT,
  GLASS_GRADIENT_END,
  GLASS_GRADIENT_START,
  softHalo,
} from './landingStyleKit';

interface TileProps {
  icon: ComponentType<{ size?: number; color?: string; strokeWidth?: number }>;
  titleKey: string;
  descriptionKey: string;
  children?: ReactNode;
}

const GlassTile = ({ icon, titleKey, descriptionKey, children }: TileProps) => {
  const { t } = useTranslation();
  return (
    <Pressable
      focusable={false}
      accessibilityRole="none"
      style={(state) => [
        tileStyles.tile,
        (state as { hovered?: boolean }).hovered && tileStyles.tileHover,
      ]}
    >
      <LinearGradient
        colors={GLASS_GRADIENT}
        start={GLASS_GRADIENT_START}
        end={GLASS_GRADIENT_END}
        style={StyleSheet.absoluteFill}
      />
      <View style={tileStyles.body}>
        <BrandIcon icon={icon} size="md" />
        <View style={tileStyles.copy}>
          <Text variant="h4" aria-level={3} style={tileStyles.title}>
            {t(titleKey)}
          </Text>
          <Text variant="body" color={colors.text.secondary} style={tileStyles.description}>
            {t(descriptionKey)}
          </Text>
        </View>
        {children ? <View style={tileStyles.visual}>{children}</View> : null}
      </View>
    </Pressable>
  );
};

const SourceFlow = () => {
  const { t } = useTranslation();
  const chips: { label: string; color: string }[] = [
    { label: t('landing.features.sourceFlow.linkedin'), color: colors.brand.linkedin },
    { label: t('landing.features.sourceFlow.email'), color: colors.primary[400] },
    { label: t('landing.features.sourceFlow.webForm'), color: colors.accent.purple[400] },
  ];
  return (
    <View style={flowStyles.row}>
      {chips.map((chip) => (
        <View key={chip.label} style={flowStyles.chip}>
          <View style={[flowStyles.chipDot, { backgroundColor: chip.color }]} />
          <Text variant="caption" color={colors.text.secondary}>
            {chip.label}
          </Text>
        </View>
      ))}
      <ArrowRight size={16} color={colors.text.tertiary} strokeWidth={2} />
      <View style={flowStyles.enriched}>
        <Bot size={13} color={colors.primary[300]} strokeWidth={2} />
        <Text variant="caption" color={colors.primary[300]}>
          {t('landing.features.sourceFlow.enrichedLead')}
        </Text>
      </View>
    </View>
  );
};

const MiniBars = () => {
  const data = [40, 58, 47, 72, 65, 88, 79, 96, 84, 100];
  const max = Math.max(...data);
  return (
    <View style={barStyles.row}>
      {data.map((value, index) => (
        <View
          key={`${value}-${index}`}
          style={[
            barStyles.bar,
            {
              height: `${(value / max) * 100}%`,
              backgroundColor:
                index === data.length - 1 ? colors.primary[400] : colors.primary.subtle,
            },
          ]}
        />
      ))}
    </View>
  );
};

export const PremiumFeaturesSection = () => {
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const isDesktop = width >= tokens.breakpoints.desktop;

  const leadCapture = (
    <GlassTile
      icon={Sparkles}
      titleKey="landing.features.items.aiLeadCapture.title"
      descriptionKey="landing.features.items.aiLeadCapture.description"
    >
      <SourceFlow />
    </GlassTile>
  );
  const qualification = (
    <GlassTile
      icon={Bot}
      titleKey="landing.features.items.smartQualification.title"
      descriptionKey="landing.features.items.smartQualification.description"
    />
  );
  const nurturing = (
    <GlassTile
      icon={Zap}
      titleKey="landing.features.items.automatedNurturing.title"
      descriptionKey="landing.features.items.automatedNurturing.description"
    />
  );
  const analytics = (
    <GlassTile
      icon={BarChart3}
      titleKey="landing.product.items.analytics.title"
      descriptionKey="landing.product.items.analytics.description"
    >
      <MiniBars />
    </GlassTile>
  );

  return (
    <View style={styles.section}>
      <Container>
        <ScrollReveal style={styles.headerContainer}>
          <LandingSectionHeading
            eyebrow={t('landing.features.badge')}
            title={t('landing.features.title')}
            accent={t('landing.features.accent')}
            subtitle={t('landing.features.subtitle')}
          />
        </ScrollReveal>

        {isDesktop ? (
          <View style={styles.bento}>
            <View style={styles.bentoRow}>
              <View style={styles.wide}>{leadCapture}</View>
              <View style={styles.narrow}>{qualification}</View>
            </View>
            <View style={styles.bentoRow}>
              <View style={styles.narrow}>{nurturing}</View>
              <View style={styles.wide}>{analytics}</View>
            </View>
          </View>
        ) : (
          <View style={styles.stack}>
            {leadCapture}
            {qualification}
            {nurturing}
            {analytics}
          </View>
        )}
      </Container>
    </View>
  );
};

const styles = StyleSheet.create({
  section: {
    backgroundColor: colors.transparent,
    paddingVertical: Platform.select({ web: 96, default: 64 }),
  },
  headerContainer: {
    alignItems: 'center',
    marginBottom: Platform.select({ web: 48, default: 40 }),
  },
  bento: {
    gap: spacing.lg,
  },
  bentoRow: {
    flexDirection: 'row',
    gap: spacing.lg,
    alignItems: 'stretch',
  },
  wide: {
    flex: 2,
    minWidth: 0,
  },
  narrow: {
    flex: 1,
    minWidth: 0,
  },
  stack: {
    gap: spacing.lg,
  },
});

const tileStyles = StyleSheet.create({
  tile: {
    flex: 1,
    minHeight: 200,
    borderRadius: tokens.radius.xl,
    borderWidth: 1,
    borderColor: colors.border.muted,
    overflow: 'hidden',
    backgroundColor: colors.surface.row,
  },
  tileHover: {
    borderColor: colors.primary.borderActive,
    transform: [{ translateY: -3 }],
    ...softHalo,
  },
  body: {
    flex: 1,
    padding: spacing.xl,
    gap: spacing.md,
  },
  copy: {
    gap: spacing.xs,
  },
  title: {
    fontSize: Platform.select({ web: 19, default: 18 }),
    fontWeight: '600',
    color: colors.text.primary,
  },
  description: {
    fontSize: Platform.select({ web: 14, default: 14 }),
    lineHeight: 22,
    maxWidth: 440,
  },
  visual: {
    marginTop: 'auto',
    paddingTop: spacing.md,
  },
});

const flowStyles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.md,
    borderRadius: tokens.radius.full,
    borderWidth: 1,
    borderColor: colors.border.muted,
    backgroundColor: colors.background.secondary,
  },
  chipDot: {
    width: 7,
    height: 7,
    borderRadius: 4,
  },
  enriched: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.md,
    borderRadius: tokens.radius.full,
    borderWidth: 1,
    borderColor: colors.primary.borderActive,
    backgroundColor: colors.primary.muted,
  },
});

const barStyles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 6,
    height: 64,
  },
  bar: {
    flex: 1,
    borderTopLeftRadius: 3,
    borderTopRightRadius: 3,
  },
});
